import { useCallback } from "react";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { casesService } from "../services/cases.service";
import {
  getCasePrefixReassignmentError,
  type ReassignCasePrefixRequest,
} from "../types/casePrefixReassignment";
import { useHasPermission } from "./useHasPermission";

const REASSIGN_PERMISSION = "cases.prefix.reassign";

interface UseCasePrefixReassignmentOptions {
  enabled: boolean;
}

export function useCasePrefixReassignment(
  caseId: string,
  { enabled }: UseCasePrefixReassignmentOptions
) {
  const queryClient = useQueryClient();
  const canReassignPrefix = useHasPermission(REASSIGN_PERMISSION);
  const contextQuery = useQuery({
    queryKey: ["case-prefix-reassignment-context", caseId],
    queryFn: () => casesService.getPrefixReassignmentContext(caseId),
    enabled: enabled && canReassignPrefix && Boolean(caseId),
  });

  const invalidateCaseIdentity = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["case", caseId],
        exact: true,
      }),
      queryClient.invalidateQueries({ queryKey: ["cases"], exact: false }),
      queryClient.invalidateQueries({
        queryKey: ["case-prefix-reassignment-context", caseId],
        exact: true,
      }),
    ]);
  }, [caseId, queryClient]);

  const reassignMutation = useMutation({
    mutationFn: (request: ReassignCasePrefixRequest) =>
      casesService.reassignCasePrefix(caseId, request),
    onSuccess: (response) => {
      void invalidateCaseIdentity();
      notifications.show({
        title: "Case prefix changed",
        message: `${response.previousIdentity.caseNumber} is now ${response.currentIdentity.caseNumber}. Reprint any existing labels.`,
        color: "green",
        autoClose: false,
      });
    },
    onError: (error) => {
      const reassignmentError = getCasePrefixReassignmentError(error);
      if (reassignmentError?.code === "CASE_PREFIX_REASSIGNMENT_STALE") {
        void contextQuery.refetch();
      }
    },
  });

  return {
    canReassignPrefix,
    contextQuery,
    reassignMutation,
  };
}
